import { Divider, Error_Component } from '../../misc'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core'
import { useEffect, useState } from 'react'
import { DraggableCardTaskProps } from '../../../interfaces/draggable_types'
import { EmployeeNameEmail } from '../../../interfaces/employee_type'
import { getAllEmployeeNameAndEmail } from '../../../api/employee'

import { DraggableCard } from './Components/Draggable'
import { DynamicTaskDroppableContainer } from './Components/Droppable'
import useTaskDragNDrop from '../../../hooks/useTaskDragNDrop'
import { getAllTaskTemplates } from '../../../api/task_template'
import { useNavigate, useParams } from 'react-router-dom'
import { getProject } from '../../../api/projects'
import { ProjectProps } from '../../../interfaces/project_types'
import { BackButton, OrangeButton } from '../../Buttons'
import { TaskProps } from '../../../interfaces/tasks_types'
import { STARTING_EMPLOYEE_CONTAINERS } from './Interface/DnD'
import CreateTaskForm from './CreateTask'

export type EmployeeContainerProps = {
    employee: EmployeeNameEmail
    tasks: DraggableCardTaskProps[]
}
/**
 * TODO:
 *  - The Employee Containers should have a field for all their tasks
 *  - I could do that but the cards already have a parent field (but what's more efficient?)
 * 
 * This is the main route for the DragNDropTasks page
 * 
 * This page is used to assign tasks to employees
 * 
 * The tasks are displayed in a list of cards, and the employees are displayed in a list of droppable containers
 * 
 * The cards can be dragged and dropped into the droppable containers
 * 
 * The droppable containers have a remove button that will remove the employee from the project
 * 
 */
export default function DragNDropTasks() {
    const { id } = useParams()
    const [employee_containers, setEmployeeContainers] = useState<EmployeeContainerProps[]>(STARTING_EMPLOYEE_CONTAINERS)
    const [employee_options, setEmployeeOptions] = useState<EmployeeNameEmail[]>([])
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [activeId, setActiveId] = useState<string | null>(null)
    const [project, setProject] = useState<ProjectProps | null>(null)
    const navigate = useNavigate()

    const [cards, setCards] = useState<DraggableCardTaskProps[]>([])

    // All the functions for the drag and drop are in the useTaskDragNDrop hook
    const { handleDragEnd, handleAddEmployee, handleRemoveEmployee, handleEmployeeSelect, handleDragStart, handleOnSubmit, handleOnReset, handleToggleUnneeded } = useTaskDragNDrop({
        cards, setCards, employee_containers, setEmployeeContainers, setErrorMessage, employee_options, setEmployeeOptions, setActiveId, navigate, project_id: id || ''
    })

    useEffect(() => {
        const getBackendData = async () => {
            const employees = await getAllEmployeeNameAndEmail({ for_selection: true, filter_by_hierarchy: 'MANAGERS, TEAM, IT, ADMIN' })
            setEmployeeOptions(employees)

            const taskTemplates = await getAllTaskTemplates()

            let projectData: ProjectProps | null = null
            let unneededIds: number[] = []
            if (id) {
                const projectRes = await getProject(id)
                projectData = projectRes.data
                setProject(projectRes.data)
                unneededIds = projectRes.data.unneeded_template_ids || []
            }
            const unneededSet = new Set<number>(unneededIds)

            const isCardUnneeded = (template_id: number | null) =>
                template_id != null && unneededSet.has(template_id)

            // Convert the task templates to the format of the cards
            const cards = taskTemplates.map((task) => ({
                id: task.id,
                task_id: null,
                title: task.title,
                description: task.description || '',
                category: task.category,
                parent: null,
                selected_employee: null,
                template_id: typeof task.id === 'number' ? task.id : Number(task.id),
                is_unneeded: unneededSet.has(typeof task.id === 'number' ? task.id : Number(task.id)),
            }))
            setCards(cards)

            if (id && projectData) {
                const rawTasks: TaskProps[] = (projectData.tasks as TaskProps[]) || []
                const existingTasks: TaskProps[] = rawTasks.filter((t) => t.status === 'Active')

                if (existingTasks && existingTasks.length > 0) {
                    // For each existing task, find its matching TaskTemplate by (title, category) so we can keep
                    // the template link even after the user has assigned it.
                    const matchTemplate = (title: string, category: string) =>
                        taskTemplates.find((t) => t.title === title && t.category === category)

                    // Existing-task-derived cards. If the matching template is marked unneeded, force-unassign.
                    const newCards = existingTasks.map((task) => {
                        const matched = matchTemplate(task.title, task.category || '')
                        const template_id: number | null = matched
                            ? (typeof matched.id === 'number' ? matched.id : Number(matched.id))
                            : null
                        const unneeded = isCardUnneeded(template_id)
                        return {
                            id: task.task_id || '',
                            task_id: task.task_id || null,
                            title: task.title,
                            description: task.description || '',
                            category: task.category || '',
                            parent: unneeded ? null : (employees.find((emp) => emp.label === task.assigned_to)?.value || null),
                            selected_employee: unneeded ? null : (task.assigned_to || null),
                            template_id,
                            is_unneeded: unneeded,
                        }
                    })

                    // Combine with template cards that have no matching existing task.
                    const allCards = [...newCards]
                    cards.forEach((templateCard) => {
                        const isDupe = allCards.some(existingCard =>
                            existingCard.title === templateCard.title &&
                            existingCard.category === templateCard.category
                        )

                        if (!isDupe) {
                            allCards.push(templateCard)
                        }
                    })

                    setCards(allCards)

                    // Build employee containers only from tasks that aren't unneeded.
                    const assignableTasks = existingTasks.filter((task) => {
                        const matched = matchTemplate(task.title, task.category || '')
                        const template_id: number | null = matched
                            ? (typeof matched.id === 'number' ? matched.id : Number(matched.id))
                            : null
                        return !isCardUnneeded(template_id)
                    })

                    let assignedEmployees: string[] = assignableTasks
                        .map((task) => task.assigned_to)
                        .filter((e): e is string => e != null)
                    assignedEmployees = [...new Set(assignedEmployees)]

                    setEmployeeContainers(assignedEmployees.map((employee) => ({
                        employee: {
                            label: employee,
                            value: employees.find((emp) => emp.label === employee)?.value || '',
                            name: employee,
                            email: employees.find((emp) => emp.label === employee)?.value || ''
                        },
                        tasks: assignableTasks
                        .filter(task => task.assigned_to === employee)
                        .map((task) => {
                            const matched = matchTemplate(task.title, task.category || '')
                            const template_id: number | null = matched
                                ? (typeof matched.id === 'number' ? matched.id : Number(matched.id))
                                : null
                            return {
                                id: task.task_id || '',
                                task_id: task.task_id || null,
                                title: task.title || '',
                                description: task.description || '',
                                category: task.category || '',
                                parent: task.assigned_to || null,
                                selected_employee: task.assigned_to || null,
                                template_id,
                                is_unneeded: false,
                            }
                        })
                    })))

                    }

            } else {
                setErrorMessage("NO PROJECT ID PROVIDED. NOTHING WILL BE SAVED. PLEASE REFRESH THE PAGE AND TRY AGAIN.")
            }
        }

        getBackendData()
    }, [])

    return (
        <div>
            <div className='flex flex-col mx-2'>
                <h1>
                    Project: {project?.project_name}
                </h1>
            </div>

            <DnDContainer cards={cards} onDragStart={handleDragStart} onDragEnd={handleDragEnd} setErrorMessage={setErrorMessage} setCards={setCards} onToggleUnneeded={handleToggleUnneeded}>
                <div className='flex items-center justify-center'>
                    {errorMessage && <Error_Component errorString={errorMessage} />}
                </div>

                <div className='flex flex-row'>
                    <DynamicTaskDroppableContainer
                        droppables={employee_containers.map((employee) => employee.employee.value)} // id's should be the emails
                        tasksCards={cards}
                        employee_options={employee_options}
                        onRemove={handleRemoveEmployee}
                        onEmployeeSelect={handleEmployeeSelect}
                        onToggleUnneeded={handleToggleUnneeded}
                    />
                </div>

                {/* This is the overlay that shows the card when it is being dragged */}
                <DragOverlay>
                    {activeId &&
                    <div className='w-full transition-all duration-300 scale-110 rotate-6'>
                        <DraggableCard
                            key={activeId}
                            id={activeId}
                            task_id={cards.find((card) => card.id === activeId)?.task_id ?? null}
                            title={cards.find((card) => card.id === activeId)?.title || ''}
                            description={cards.find((card) => card.id === activeId)?.description || ''}
                            category={cards.find((card) => card.id === activeId)?.category || ''}
                            parent={cards.find((card) => card.id === activeId)?.parent || null}
                            selected_employee={cards.find((card) => card.id === activeId)?.selected_employee || null}
                            template_id={cards.find((card) => card.id === activeId)?.template_id ?? null}
                            is_unneeded={cards.find((card) => card.id === activeId)?.is_unneeded || false}
                        />
                    </div>
                    }
                </DragOverlay>

                <div className='flex flex-row items-center justify-center gap-2'>
                    <BackButton />
                    <OrangeButton onClick={handleOnReset}>Reset</OrangeButton>
                    <OrangeButton onClick={handleAddEmployee}>Add Employee</OrangeButton>
                    <OrangeButton onClick={handleOnSubmit}>Finish</OrangeButton>
                </div>
            </DnDContainer>
        </div>
    )
    
}

/**
 * This is the container for the tasks that still need to be assigned
 *
 * It displays the tasks that still need to be assigned and the tasks that have been assigned
 */
function NonAssignedTaskContainer({cards, setCards, setErrorMessage, onToggleUnneeded}: {cards: DraggableCardTaskProps[], setCards: (cards: DraggableCardTaskProps[]) => void, setErrorMessage: (errorMessage: string) => void, onToggleUnneeded: (card_id: string) => void}) {
    return(
        <div className='flex flex-col w-full h-full p-2 my-2 border rounded-md bg-orange-50 dark:bg-stone-800 border-stone-300 dark:border-stone-700'>
            <AssignedTaskText cards={cards} />

            <Divider />

            <div className='min-h-[500px] max-h-[800px] overflow-y-auto'>
                <RenderUnassignedTasks cards={cards} onToggleUnneeded={onToggleUnneeded} />
            </div>

            <Divider />

            <CreateTaskForm cards={cards} setCards={setCards} setErrorMessage={setErrorMessage} />
        </div>
    )
}

function AssignedTaskText({cards}: {cards: DraggableCardTaskProps[]}) {
    return(
        cards.some((card) => card.parent === null && !card.is_unneeded) ?
            <h2 className='mb-2 text-xl font-bold text-red-500'>
            Tasks that still need to be assigned:
            </h2>
        : <h2 className='mb-2 text-xl font-bold text-green-500'>

            All tasks have been assigned
        </h2>
    )
}

function RenderUnassignedTasks({cards, onToggleUnneeded}: {cards: DraggableCardTaskProps[], onToggleUnneeded: (card_id: string) => void}) {
    return(
        cards.map((card) => (
            card.parent === null ?
                <DraggableCard
                    key={card.id}
                    id={card.id}
                    task_id={card.task_id}
                    title={card.title}
                    description={card.description}
                    category={card.category}
                    parent={card.parent}
                    selected_employee={card.selected_employee}
                    template_id={card.template_id}
                    is_unneeded={card.is_unneeded}
                    onToggleUnneeded={onToggleUnneeded}
                /> : null
        ))
    )
}

type DnDContainerProps = {
    children: React.ReactNode
    onDragStart: (event: DragStartEvent) => void
    onDragEnd: (event: DragEndEvent) => void
    cards: DraggableCardTaskProps[]
    setErrorMessage: (errorMessage: string) => void
    setCards: (cards: DraggableCardTaskProps[]) => void
    onToggleUnneeded: (card_id: string) => void
}

function DnDContainer({children, onDragStart, onDragEnd, cards, setErrorMessage, setCards, onToggleUnneeded}: DnDContainerProps) {
    return(

        <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <div className='flex flex-row h-full p-2 rounded-md bg-stone-50 dark:bg-stone-900'>
                <div className='flex-1 max-w-[18rem]'>
                    <NonAssignedTaskContainer cards={cards} setCards={setCards} setErrorMessage={setErrorMessage} onToggleUnneeded={onToggleUnneeded} />
                </div>

                <div className='flex-auto overflow-x-auto'>
                    {children}
                </div>
            </div>
        </DndContext>
    )
}